import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { StepContainer, NavigationButtons, InfoBox, FormField } from '../index';
import { useWizard } from '../../wizard';
import { contentApi } from '../../services/contentApi';
import { normalizeFlowId } from '../../wizard/flowRegistry';
import { CONTACT_DEFAULT_HINT, CONTACT_DEFAULT_INFO, validateContactField, normalizeContactField } from './contactInfoUtils';
import type { ContactFieldName } from './contactInfoUtils';

interface ContactErrors extends Partial<Record<ContactFieldName | 'consent', string>> {}

const ContactInfoStep: React.FC = () => {
  const { selfServiceData, updateSelfServiceData, goNext, goBack } = useWizard();
  const location = useLocation();
  const flowSegment = useMemo(() => location.pathname.split('/').filter(Boolean)[0] || null, [location.pathname]);
  const flowId = useMemo(() => normalizeFlowId(flowSegment), [flowSegment]);

  const [infoText, setInfoText] = useState<string>(CONTACT_DEFAULT_INFO);
  const [errors, setErrors] = useState<ContactErrors>({});
  const [consent, setConsent] = useState<boolean>(!!selfServiceData.contactConsent);
  const consentRef = useRef<boolean>(!!selfServiceData.contactConsent);

  useEffect(() => {
    let mounted = true;
    contentApi
      .get(flowId, 'contact_info', 'da')
      .then(content => {
        if (mounted && content?.body?.[0]) {
          setInfoText(content.body[0]);
        }
      })
      .catch(() => undefined);
    return () => {
      mounted = false;
    };
  }, [flowId]);

  useEffect(() => {
    const current = !!selfServiceData.contactConsent;
    setConsent(current);
    consentRef.current = current;
  }, [selfServiceData.contactConsent]);

  const handleFieldChange = (name: ContactFieldName, value: string) => {
    // Normalize the value (especially for phone numbers - removes +45, spaces, etc.)
    const normalizedValue = normalizeContactField(name, value);
    updateSelfServiceData(name, normalizedValue);
    const error = validateContactField(name, normalizedValue);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleConsentChange = (checked: boolean) => {
    setConsent(checked);
    consentRef.current = checked;
    updateSelfServiceData('contactConsent', checked);
    setErrors(prev => ({ ...prev, consent: checked ? undefined : prev.consent }));
  };

  const handleNext = () => {
    const nameError = validateContactField('contactName', selfServiceData.contactName || '');
    const mobilError = validateContactField('contactMobil', selfServiceData.contactMobil || '');
    const emailError = validateContactField('contactEmail', selfServiceData.contactEmail || '');
    const consentChecked = consentRef.current;

    const nextErrors: ContactErrors = {
      contactName: nameError,
      contactMobil: mobilError,
      contactEmail: emailError,
      consent: consentChecked ? undefined : 'Bekræft venligst, at vi må sende påmindelser via mail/SMS.'
    };

    const hasErrors = !!(nameError || mobilError || emailError || !consentChecked);

    if (hasErrors) {
      setErrors(nextErrors);
      return;
    }

    setErrors({});
    goNext();
  };

  const consentErrorId = errors.consent ? 'error-contactConsent' : undefined;

  return (
    <StepContainer title="Kontaktoplysninger">
      <InfoBox type="info">
        <p>{infoText || CONTACT_DEFAULT_INFO}</p>
        <p>{CONTACT_DEFAULT_HINT}</p>
      </InfoBox>

      <form
        className="contact-form"
        onSubmit={(event) => {
          event.preventDefault();
          handleNext();
        }}
        noValidate
      >
        <div className="form-field-wrapper">
          <FormField
            name="contactName"
            value={selfServiceData.contactName || ''}
            placeholder="Navn *"
            error={errors.contactName}
            onChange={(event) => handleFieldChange('contactName', event.target.value)}
            onBlur={(event) => handleFieldChange('contactName', event.target.value)}
            required
            autoComplete="name"
          />
        </div>
        <div className="form-field-wrapper">
          <FormField
            type="tel"
            name="contactMobil"
            value={selfServiceData.contactMobil || ''}
            placeholder="Mobil (8 cifre)"
            error={errors.contactMobil}
            onChange={(event) => handleFieldChange('contactMobil', event.target.value)}
            onBlur={(event) => handleFieldChange('contactMobil', event.target.value)}
            required
            autoComplete="tel"
          />
        </div>
        <div className="form-field-wrapper">
          <FormField
            type="email"
            name="contactEmail"
            value={selfServiceData.contactEmail || ''}
            placeholder="Email"
            error={errors.contactEmail}
            onChange={(event) => handleFieldChange('contactEmail', event.target.value)}
            onBlur={(event) => handleFieldChange('contactEmail', event.target.value)}
            required
            autoComplete="email"
          />
        </div>

        <div className="consent-wrapper">
          <label className="checkbox-field">
            <input
              type="checkbox"
              name="contactConsent"
              checked={consent}
              onChange={(event) => handleConsentChange(event.target.checked)}
              aria-describedby={consentErrorId}
              aria-invalid={Boolean(errors.consent)}
            />
            <span>
              Jeg accepterer, at kommunen må sende påmindelser og kvitteringer på mail/SMS i forbindelse med denne henvendelse.
              <small className="checkbox-hint">{CONTACT_DEFAULT_HINT}</small>
            </span>
          </label>
          {errors.consent && (
            <span id={consentErrorId} className="error-message error-text">
              {errors.consent}
            </span>
          )}
        </div>
      </form>

      <NavigationButtons onBack={goBack} onNext={handleNext} nextText="Fortsæt" />
    </StepContainer>
  );
};

export default ContactInfoStep;

